import apiClient from '@/api/client';
import { AUTH } from '@/api/endpoints';
import type {
  LoginRequest,
  LoginResponse,
  RegisterRequest,
  RegisterResponse,
  RefreshTokenResponse,
  ChangePasswordRequest,
  RequestOtpResponse,
  VerifyOtpResponse,
  GoogleLoginResponse,
  User,
} from '@/api/types/auth.types';

/** Authenticate with email or phone + password and receive tokens + user. */
export async function login(credentials: LoginRequest): Promise<LoginResponse> {
  const { data } = await apiClient.post<LoginResponse>(AUTH.LOGIN, credentials);
  return data;
}

/** Register a new user and receive tokens + user. */
export async function register(payload: RegisterRequest): Promise<RegisterResponse> {
  const { data } = await apiClient.post<RegisterResponse>(AUTH.REGISTER, payload);
  return data;
}

/** Exchange a refresh token for a new access token. */
export async function refreshToken(refresh: string): Promise<RefreshTokenResponse> {
  const { data } = await apiClient.post<RefreshTokenResponse>(AUTH.REFRESH, {
    refresh,
  });
  return data;
}

/** Server-side logout (blacklist the refresh token). */
export async function logout(refresh: string): Promise<void> {
  await apiClient.post(AUTH.LOGOUT, { refresh });
}

/** Fetch the currently authenticated user's profile. */
export async function getMe(): Promise<User> {
  const { data } = await apiClient.get<User>(AUTH.ME);
  return data;
}

/** Partially update the current user's profile. */
export async function updateMe(payload: Partial<User>): Promise<User> {
  const { data } = await apiClient.patch<User>(AUTH.ME, payload);
  return data;
}

/** Change the current user's password. */
export async function changePassword(
  oldPassword: string,
  newPassword: string,
): Promise<void> {
  await apiClient.post(AUTH.CHANGE_PASSWORD, {
    old_password: oldPassword,
    new_password: newPassword,
    new_password_confirm: newPassword,
  } satisfies ChangePasswordRequest);
}

/** Request an OTP verification call to the given phone number. */
export async function requestOtp(phone: string): Promise<RequestOtpResponse> {
  const { data } = await apiClient.post<RequestOtpResponse>(AUTH.REQUEST_OTP, { phone });
  return data;
}

/** Verify the OTP code and receive JWT tokens. */
export async function verifyOtp(userId: string, code: string): Promise<VerifyOtpResponse> {
  const { data } = await apiClient.post<VerifyOtpResponse>(AUTH.VERIFY_OTP, { user_id: userId, code });
  return data;
}

/** Authenticate via Google OAuth credential. */
export async function googleLogin(credential: string): Promise<GoogleLoginResponse> {
  const { data } = await apiClient.post<GoogleLoginResponse>(AUTH.GOOGLE, { credential });
  return data;
}
